<template>
  <v-card class="historico-card" flat>
    <v-card-title class="pa-4 historico-header">
      <div class="d-flex align-center flex-grow-1">
        <v-icon color="#023449" class="mr-2">mdi-history</v-icon>
        <span class="historico-title">Histórico de Consultas</span>
      </div>
      <v-spacer></v-spacer>

      <!-- Chip de Total de Registros -->
      <v-chip
          v-if="historico.length > 0"
          small
          color="#e8f5e9"
          text-color="#2e7d32"
          class="mr-3"
      >
        <v-icon left x-small color="#2e7d32">mdi-database</v-icon>
        {{ historico.length }} {{ historico.length === 1 ? 'registro' : 'registros' }}
      </v-chip>

      <!-- Botão Toggle Filtros -->
      <v-btn
          color="#023449"
          outlined
          small
          class="mr-2 filtro-toggle-btn"
          @click="mostrarFiltros = !mostrarFiltros"
      >
        <v-icon left small>mdi-filter-variant</v-icon>
        Filtros
        <v-icon right small>{{ mostrarFiltros ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </v-btn>

      <!-- Botão Atualizar -->
      <v-btn
          color="#023449"
          dark
          icon
          class="elevation-0"
          @click="$emit('atualizar')"
          :loading="loading"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <!-- Painel de Filtros (Expansível) -->
    <v-expand-transition>
      <div v-show="mostrarFiltros" class="filtros-panel">
        <v-divider></v-divider>
        <div class="pa-4">
          <v-row>
            <!-- Filtro de Filial -->
            <v-col v-if="permissao('sortercurve_filtro_empresas')" cols="12" md="4">
              <div class="filtro-section">
                <div class="filtro-header">
                  <v-icon color="#023449" size="18">mdi-office-building</v-icon>
                  <span class="filtro-titulo">Filial</span>
                </div>
                <v-autocomplete
                    v-model="filtros.idfilial"
                    :items="filiais"
                    item-text="filial"
                    item-value="idfilial"
                    label="Todas as filiais"
                    prepend-inner-icon="mdi-domain"
                    outlined
                    dense
                    clearable
                    hide-details
                    :disabled="loading"
                    @change="emitFiltroAlterado()"
                    class="filtro-input"
                >
                  <template v-slot:selection="{ item }">
                    <span class="filtro-selecionado">{{ item.filial }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title class="filtro-item">
                        <v-icon small color="#023449" class="mr-2">mdi-domain</v-icon>
                        {{ item.filial }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption grey--text text-center py-2">
                          <v-icon small color="#94a3b8" class="mr-2">mdi-office-building-outline</v-icon>
                          Nenhuma filial disponível
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>

            <!-- Filtro de Período -->
            <v-col cols="12" :md="permissao('sortercurve_filtro_empresas') ? 8 : 12">
              <div class="filtro-section">
                <div class="filtro-header">
                  <v-icon color="#023449" size="18">mdi-calendar-range</v-icon>
                  <span class="filtro-titulo">Período de Consulta</span>
                </div>

                <v-row no-gutters class="periodo-row">
                  <v-col cols="12" sm="6" class="pr-sm-2">
                    <v-menu
                        v-model="menuDataInicio"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :value="formatarDataFiltro(filtros.dataInicio)"
                            label="Data inicial"
                            prepend-inner-icon="mdi-calendar-start"
                            readonly
                            outlined
                            dense
                            hide-details
                            class="filtro-input"
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                          v-model="filtros.dataInicio"
                          @input="menuDataInicio = false; emitFiltroAlterado()"
                          locale="pt-BR"
                          color="#023449"
                          :max="filtros.dataFim"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" sm="6" class="pl-sm-2 mt-3 mt-sm-0">
                    <v-menu
                        v-model="menuDataFim"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :value="formatarDataFiltro(filtros.dataFim)"
                            label="Data final"
                            prepend-inner-icon="mdi-calendar-end"
                            readonly
                            outlined
                            dense
                            hide-details
                            class="filtro-input"
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                          v-model="filtros.dataFim"
                          @input="menuDataFim = false; emitFiltroAlterado()"
                          locale="pt-BR"
                          color="#023449"
                          :min="filtros.dataInicio"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>

          <!-- Botão Limpar Filtros -->
          <v-row v-if="filtrosAtivos">
            <v-col cols="12" class="pt-2">
              <v-btn
                  text
                  small
                  color="#64748b"
                  class="limpar-filtros-btn"
                  @click="limparFiltros"
              >
                <v-icon left small>mdi-filter-off</v-icon>
                Limpar Filtros
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-expand-transition>

    <v-divider></v-divider>

    <!-- Loading Skeleton -->
    <div v-if="loading" class="loading-skeleton">
      <div class="pa-4">
        <div class="skeleton-table-header mb-2">
          <div v-for="n in headersVisiveis.length" :key="n" class="skeleton-header-cell">
            <v-skeleton-loader type="text" width="80%" class="skeleton-custom"></v-skeleton-loader>
          </div>
        </div>
        <v-divider class="mb-3"></v-divider>
        <div v-for="row in 5" :key="row" class="skeleton-row mb-3">
          <div class="skeleton-cell skeleton-cell-date">
            <v-skeleton-loader type="text" width="90%" class="skeleton-custom mb-1"></v-skeleton-loader>
            <v-skeleton-loader type="text" width="70%" class="skeleton-custom"></v-skeleton-loader>
          </div>
          <div class="skeleton-cell skeleton-cell-cliente">
            <v-skeleton-loader type="text" width="85%" class="skeleton-custom mb-1"></v-skeleton-loader>
            <v-skeleton-loader type="text" width="60%" class="skeleton-custom"></v-skeleton-loader>
          </div>
          <div class="skeleton-cell skeleton-cell-valor">
            <v-skeleton-loader type="text" width="75%" class="skeleton-custom"></v-skeleton-loader>
          </div>
          <div class="skeleton-cell skeleton-cell-valor">
            <v-skeleton-loader type="text" width="75%" class="skeleton-custom"></v-skeleton-loader>
          </div>
          <div class="skeleton-cell skeleton-cell-situacao">
            <v-skeleton-loader type="avatar" class="skeleton-custom-avatar"></v-skeleton-loader>
          </div>
          <!-- Coluna Usuário: só aparece no skeleton se lgAndUp -->
          <div v-if="$vuetify.breakpoint.lgAndUp" class="skeleton-cell skeleton-cell-info">
            <v-skeleton-loader type="text" width="80%" class="skeleton-custom"></v-skeleton-loader>
          </div>
          <div class="skeleton-cell skeleton-cell-info">
            <v-skeleton-loader type="text" width="75%" class="skeleton-custom"></v-skeleton-loader>
          </div>
          <div class="skeleton-cell skeleton-cell-acoes">
            <v-skeleton-loader type="button" width="32px" class="skeleton-custom-button mr-1"></v-skeleton-loader>
            <v-skeleton-loader type="button" width="32px" class="skeleton-custom-button mr-1"></v-skeleton-loader>
            <v-skeleton-loader type="button" width="32px" class="skeleton-custom-button"></v-skeleton-loader>
          </div>
        </div>
      </div>
    </div>

    <!-- Mensagem Sem Dados -->
    <div v-else-if="!loading && historico.length === 0" class="no-data-message">
      <v-icon size="64" color="#cbd5e1">mdi-history</v-icon>
      <h3>Nenhuma consulta realizada ainda</h3>
      <p>As consultas de limite de crédito aparecerão aqui</p>
    </div>

    <!-- Tabela de Histórico -->
    <v-data-table
        v-else
        :headers="headersVisiveis"
        :items="historico"
        :loading="loading"
        :items-per-page="20"
        :footer-props="{
          'items-per-page-text': 'Itens por página:',
          'items-per-page-options': [10, 20, 50, { text: 'Todos', value: -1 }]
        }"
        class="historico-table elevation-0"
        loading-text="Carregando histórico..."
        no-data-text="Nenhuma consulta realizada ainda"
    >
      <!-- Data/Hora -->
      <template v-slot:item.data_criacao="{ item }">
        <div class="celula-data">
          <div class="data-linha">
            <v-icon x-small color="#64748b" class="mr-1">mdi-calendar</v-icon>
            <span class="data-texto">{{ formatarData(item.data_criacao) }}</span>
          </div>
          <div class="data-linha">
            <v-icon x-small color="#94a3b8" class="mr-1">mdi-clock-outline</v-icon>
            <span class="hora-texto">{{ formatarHora(item.data_criacao) }}</span>
          </div>
        </div>
      </template>

      <!-- Cliente -->
      <template v-slot:item.nome_cliente="{ item }">
        <div class="celula-cliente">
          <div class="cliente-linha-principal">
            <v-avatar
                :color="item.tipo === 'CPF' ? '#dbeafe' : '#d1fae5'"
                size="32"
                class="mr-2"
            >
              <v-icon
                  x-small
                  :color="item.tipo === 'CPF' ? '#1e40af' : '#065f46'"
              >
                {{ item.tipo === 'CPF' ? 'mdi-account' : 'mdi-domain' }}
              </v-icon>
            </v-avatar>
            <div class="cliente-info">
              <div class="cliente-nome">{{ item.nome_cliente }}</div>
              <div class="cliente-documento">{{ formatarCpfCnpj(item.documento) }}</div>
            </div>
          </div>
        </div>
      </template>

      <!-- Valor Solicitado -->
      <template v-slot:item.valor_venda="{ item }">
        <div class="celula-valor">
          <div class="valor-card-mini">
            <div class="valor-content">
              <span class="valor-moeda">R$</span>
              <span class="valor-numero">{{ formatarValorNumero(item.valor_venda) }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Valor Limite Atual -->
      <template v-slot:item.valor_limite_atual="{ item }">
        <div class="celula-valor">
          <div class="valor-card-mini limite-atual">
            <div class="valor-content">
              <span class="valor-moeda">R$</span>
              <span class="valor-numero">{{ formatarValorNumero(item.valor_limite_atual) }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Situação -->
      <template v-slot:item.decisao_texto="{ item }">
        <div class="d-flex justify-center">
          <v-tooltip bottom v-if="item.decisao_texto" :color="getDecisaoColor(item.decisao_texto)">
            <template v-slot:activator="{ on, attrs }">
              <div class="situacao-wrapper" v-bind="attrs" v-on="on">
                <v-avatar
                    :color="getDecisaoColor(item.decisao_texto)"
                    size="44"
                    class="situacao-avatar"
                >
                  <v-icon color="white" size="24">
                    {{ getDecisaoIcon(item.decisao_texto) }}
                  </v-icon>
                </v-avatar>
              </div>
            </template>
            <div class="tooltip-content">
              <div class="tooltip-title">{{ item.decisao_texto }}</div>
            </div>
          </v-tooltip>

          <v-tooltip bottom v-else color="grey">
            <template v-slot:activator="{ on, attrs }">
              <div class="situacao-wrapper" v-bind="attrs" v-on="on">
                <v-avatar color="grey lighten-1" size="44" class="situacao-avatar">
                  <v-icon color="white" size="24">mdi-clock-outline</v-icon>
                </v-avatar>
              </div>
            </template>
            <div class="tooltip-content">
              <div class="tooltip-title">{{ item.status }}</div>
              <div class="tooltip-subtitle">Aguardando retorno</div>
            </div>
          </v-tooltip>
        </div>
      </template>

      <!-- Usuário -->
      <template v-slot:item.usuario="{ item }">
        <div class="celula-usuario">
          <span class="usuario-texto sentence-case">{{ item.usuario || '—' }}</span>
        </div>
      </template>

      <!-- Vendedor/Filial -->
      <template v-slot:item.vendedor="{ item }">
        <div class="celula-filial">
          <span class="usuario-texto sentence-case">{{ item.vendedor || '—' }}</span>
        </div>
      </template>

      <!-- Ações -->
      <template v-slot:item.acoes="{ item }">
        <div class="celula-acoes">
          <!-- PDF Simplificado -->
          <v-tooltip bottom color="primary">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  small
                  color="#023449"
                  class="action-btn elevation-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="$emit('visualizar-pdf', item.analise_id, false)"
                  :loading="loadingPDF === `${item.analise_id}-simples`"
              >
                <v-icon size="18">mdi-file-eye-outline</v-icon>
              </v-btn>
            </template>
            <div class="tooltip-content">
              <div class="tooltip-title">Relatório Simplificado</div>
              <div class="tooltip-subtitle">Visualização rápida</div>
            </div>
          </v-tooltip>

          <!-- PDF Detalhado -->
          <v-tooltip bottom color="primary">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  small
                  color="#023449"
                  class="action-btn elevation-1 ml-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="$emit('visualizar-pdf', item.analise_id, true)"
                  :loading="loadingPDF === `${item.analise_id}-detalhado`"
              >
                <v-icon size="18">mdi-file-document-outline</v-icon>
              </v-btn>
            </template>
            <div class="tooltip-content">
              <div class="tooltip-title">Relatório Detalhado</div>
              <div class="tooltip-subtitle">Análise completa</div>
            </div>
          </v-tooltip>

          <!-- Reavaliação -->
          <v-tooltip bottom color="#472dc6">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  small
                  color="#472dc6"
                  class="action-btn action-btn-reav elevation-1 ml-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="abrirDialogReavaliacao(item)"
                  :disabled="!podeReaValiar(item) || loadingReavaliacaoBtn === item.analise_id"
                  :loading="loadingReavaliacaoBtn === item.analise_id"
              >
                <v-icon size="18">mdi-refresh-circle</v-icon>
              </v-btn>
            </template>
            <div class="tooltip-content">
              <div class="tooltip-title">Solicitar Reavaliação</div>
              <div class="tooltip-subtitle">
                {{ podeReaValiar(item) ? 'Reenviar para análise' : 'Não disponível para este status' }}
              </div>
            </div>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <!-- ======================================================
         Dialog de Reavaliação
    ====================================================== -->
    <v-dialog v-model="dialogReavaliacao.show" max-width="520" persistent>
      <v-card class="reav-card">
        <!-- Header -->
        <div class="reav-header">
          <div class="reav-header-icon">
            <v-icon color="white" size="28">mdi-refresh-circle</v-icon>
          </div>
          <div class="reav-header-text">
            <div class="reav-header-titulo">Solicitar Reavaliação</div>
            <div class="reav-header-subtitulo" v-if="dialogReavaliacao.item">
              {{ dialogReavaliacao.item.nome_cliente }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="fecharDialogReavaliacao" :disabled="loadingReavaliacao">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="reav-body">
          <!-- Info da Análise -->
          <div class="reav-info-box" v-if="dialogReavaliacao.item">
            <div class="reav-info-row">
              <div class="reav-info-item">
                <span class="reav-info-label">Cliente</span>
                <span class="reav-info-valor">{{ dialogReavaliacao.item.nome_cliente }}</span>
              </div>
              <div class="reav-info-item">
                <span class="reav-info-label">Documento</span>
                <span class="reav-info-valor">{{ formatarCpfCnpj(dialogReavaliacao.item.documento) }}</span>
              </div>
            </div>
            <div class="reav-info-row mt-2">
              <div class="reav-info-item">
                <span class="reav-info-label">Situação Atual</span>
                <v-chip
                    x-small
                    :color="getDecisaoColor(dialogReavaliacao.item.decisao_texto)"
                    text-color="white"
                    class="font-weight-bold"
                >
                  <v-icon left x-small>{{ getDecisaoIcon(dialogReavaliacao.item.decisao_texto) }}</v-icon>
                  {{ dialogReavaliacao.item.decisao_texto || dialogReavaliacao.item.status }}
                </v-chip>
              </div>
              <div class="reav-info-item">
                <span class="reav-info-label">Limite Solicitado</span>
                <span class="reav-info-valor reav-valor-destaque">
                  R$ {{ formatarValorNumero(dialogReavaliacao.item.valor_venda) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Campo Justificativa -->
          <div class="reav-justificativa-label mt-4 mb-2">
            <v-icon color="#472dc6" size="16" class="mr-1">mdi-text-box-edit-outline</v-icon>
            <span>Justificativa <span class="reav-obrigatorio">*</span></span>
          </div>

          <v-textarea
              v-model="dialogReavaliacao.justificativa"
              placeholder="Descreva o motivo da reavaliação. Ex: cliente apresentou documentação complementar, houve atualização cadastral, etc."
              outlined
              dense
              rows="4"
              counter="500"
              maxlength="500"
              :rules="[v => !!v && v.trim().length >= 10 || 'Mínimo de 10 caracteres']"
              :error-messages="justificativaErro"
              :disabled="loadingReavaliacao"
              class="reav-textarea"
              no-resize
              @input="justificativaErro = ''"
          ></v-textarea>

          <!-- Aviso -->
          <div class="reav-aviso">
            <v-icon color="#f59e0b" size="16" class="mr-1">mdi-alert-outline</v-icon>
            <span>A análise retornará ao status <strong>Reanálise</strong> e será reprocessada pela Sorter Curve.</span>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="reav-actions">
          <v-btn
              text
              color="#64748b"
              :disabled="loadingReavaliacao"
              @click="fecharDialogReavaliacao"
              class="reav-btn-cancelar"
          >
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
              color="#472dc6"
              depressed
              :loading="loadingReavaliacao"
              :disabled="!justificativaValida"
              @click="confirmarReavaliacao"
              class="reav-btn-confirmar white--text"
          >
            <v-icon left small>mdi-send</v-icon>
            Solicitar Reavaliação
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'ConsultaLimiteTabela',

  props: {
    filiais: {
      type: Array,
      default: () => []
    },
    historico: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingPDF: {
      type: String,
      default: null
    }
  },

  data() {
    const hoje = new Date();
    const dataInicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().substr(0, 10);
    const dataFimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().substr(0, 10);

    return {
      mostrarFiltros: false,
      filtros: {
        idfilial: null,
        dataInicio: dataInicioMes,
        dataFim: dataFimMes,
      },
      filtrosPadrao: {
        idfilial: null,
        dataInicio: dataInicioMes,
        dataFim: dataFimMes,
      },
      menuDataInicio: false,
      menuDataFim: false,

      // Estado do Dialog de Reavaliação
      dialogReavaliacao: {
        show: false,
        item: null,
        justificativa: '',
      },
      loadingReavaliacao: false,
      loadingReavaliacaoBtn: null,
      justificativaErro: '',

      allHeaders: [
        { text: 'Data/Hora',         value: 'data_criacao',       width: '80px',  sortable: true,  align: undefined,  minBreakpoint: null },
        { text: 'Cliente',           value: 'nome_cliente',       width: '300px', sortable: false, align: undefined,  minBreakpoint: null },
        { text: 'Limite Solicitado', value: 'valor_venda',        width: '110px', sortable: false, align: 'center',   minBreakpoint: null },
        { text: 'Limite Atual',      value: 'valor_limite_atual', width: '110px', sortable: false, align: 'center',   minBreakpoint: null },
        { text: 'Situação',          value: 'decisao_texto',      width: '80px',  sortable: false, align: 'center',   minBreakpoint: null },
        { text: 'Usuário',           value: 'usuario',            width: '110px', sortable: true,  align: undefined,  minBreakpoint: 'lg' },
        { text: 'Filial',            value: 'vendedor',           width: '110px', sortable: true,  align: undefined,  minBreakpoint: null },
        { text: 'Ações',             value: 'acoes',              width: '100px', sortable: false, align: 'center',   minBreakpoint: null },
      ],
    };
  },

  computed: {
    filtrosAtivos() {
      return (
          this.filtros.idfilial !== this.filtrosPadrao.idfilial ||
          this.filtros.dataInicio !== this.filtrosPadrao.dataInicio ||
          this.filtros.dataFim !== this.filtrosPadrao.dataFim
      );
    },

    justificativaValida() {
      const j = (this.dialogReavaliacao.justificativa || '').trim();
      return j.length >= 10;
    },

    headersVisiveis() {
      const isLgAndUp = this.$vuetify.breakpoint.lgAndUp;

      return this.allHeaders
          .filter(h => {
            if (!h.minBreakpoint) return true;
            if (h.minBreakpoint === 'lg') return isLgAndUp;
            return true;
          })
          .map(({ ...rest }) => {
            // Remove chaves com valor undefined para evitar warnings no v-data-table
            return Object.fromEntries(
                Object.entries(rest).filter(([, v]) => v !== undefined)
            );
          });
    },
  },

  methods: {
    // ── Reavaliação ──────────────────────────────────────────

    podeReaValiar(item) {
      const decisoesPermitidas = [1, 4, 5];
      return decisoesPermitidas.includes(item.decisao);
    },

    abrirDialogReavaliacao(item) {
      this.dialogReavaliacao = {
        show: true,
        item,
        justificativa: '',
      };
      this.justificativaErro = '';
    },

    fecharDialogReavaliacao() {
      this.dialogReavaliacao.show = false;
      this.dialogReavaliacao.item = null;
      this.dialogReavaliacao.justificativa = '';
      this.justificativaErro = '';
    },

    async confirmarReavaliacao() {
      if (!this.justificativaValida) {
        this.justificativaErro = 'A justificativa deve ter pelo menos 10 caracteres.';
        return;
      }

      const analiseId = this.dialogReavaliacao.item.analise_id;

      this.loadingReavaliacao = true;
      this.loadingReavaliacaoBtn = analiseId;
      try {
        await new Promise((resolve, reject) => {
          this.$emit('solicitar-reavaliacao', {
            analise_id:    analiseId,
            justificativa: this.dialogReavaliacao.justificativa.trim(),
            resolve,
            reject,
          });
        });
        this.fecharDialogReavaliacao();
      } catch {
        // erro já tratado pelo pai via snackbar
      } finally {
        this.loadingReavaliacao = false;
        this.loadingReavaliacaoBtn = null;
      }
    },

    // ── Filtros ──────────────────────────────────────────────

    emitFiltroAlterado() {
      this.$emit('filtro-alterado', {
        idfilial:   this.filtros.idfilial,
        dataInicio: `${this.filtros.dataInicio} 00:00:00`,
        dataFim:    `${this.filtros.dataFim} 23:59:59`,
      });
    },

    limparFiltros() {
      this.filtros = {
        idfilial:   null,
        dataInicio: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().substr(0, 10),
        dataFim:    new Date().toISOString().substr(0, 10),
      };
      this.emitFiltroAlterado();
    },

    // ── Formatação ───────────────────────────────────────────

    formatarValorNumero(valor) {
      if (!valor) return '0,00';
      return parseFloat(valor).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatarDataFiltro(data) {
      if (!data) return '-';
      const [ano, mes, dia] = data.split('-');
      return `${dia}/${mes}/${ano}`;
    },

    formatarCpfCnpj(valor) {
      if (!valor) return '-';
      const numeros = valor.replace(/\D/g, '');
      if (numeros.length === 11)
        return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      if (numeros.length === 14)
        return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      return valor;
    },

    formatarData(dataHora) {
      if (!dataHora) return '-';
      try {
        const dataISO = dataHora.substring(0, 19).replace(/-(\d{2})\./, 'T$1:').replace(/\./, ':');
        const data = new Date(dataISO);
        if (isNaN(data.getTime())) return '-';
        return data.toLocaleDateString('pt-BR');
      } catch { return '-'; }
    },

    formatarHora(dataHora) {
      if (!dataHora) return '-';
      try {
        const dataISO = dataHora.substring(0, 19).replace(/-(\d{2})\./, 'T$1:').replace(/\./, ':');
        const data = new Date(dataISO);
        if (isNaN(data.getTime())) return '-';
        return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      } catch { return '-'; }
    },

    getDecisaoColor(decisao) {
      if (!decisao) return 'grey';
      const d = decisao.toLowerCase();
      if (d.includes('recomendado') && !d.includes('não')) return '#22c55e';
      if (d.includes('não recomendado'))   return '#ef4444';
      if (d.includes('reanálise'))         return '#472dc6';
      if (d.includes('análise'))           return '#f59e0b';
      if (d.includes('não concluída'))     return '#6b7280';
      return '#3b82f6';
    },

    getDecisaoIcon(decisao) {
      if (!decisao) return 'mdi-help-circle';
      const d = decisao.toLowerCase();
      if (d.includes('recomendado manual') && !d.includes('não')) return 'mdi-account-check';
      if (d.includes('recomendado') && !d.includes('não'))        return 'mdi-thumb-up';
      if (d.includes('não recomendado manual'))                   return 'mdi-account-remove';
      if (d.includes('não recomendado'))                          return 'mdi-thumb-down';
      if (d.includes('reanálise'))                                return 'mdi-refresh-circle';
      if (d.includes('jurídica'))                                 return 'mdi-gavel';
      if (d.includes('em análise'))                               return 'mdi-exclamation-thick';
      if (d.includes('não concluída'))                            return 'mdi-alert-circle';
      return 'mdi-information';
    },
  },
};
</script>

<style scoped>
.historico-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
  border: 1px solid #e2e8f0;
}
.historico-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #023449;
  letter-spacing: -0.01em;
}
.historico-header { background-color: #fafafa; }

.filtro-toggle-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  border-radius: 8px;
  transition: all 0.2s ease;
}
.filtro-toggle-btn:hover {
  background-color: rgba(2, 52, 73, 0.05);
  transform: translateY(-1px);
}
.filtros-panel { background: #f8fafc; }
.filtro-section { display: flex; flex-direction: column; gap: 12px; }
.filtro-header { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.filtro-titulo {
  font-size: 0.75rem;
  font-weight: 700;
  color: #023449;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.periodo-row { gap: 0; }
.filtro-input { font-size: 0.938rem; }
::v-deep .filtro-input .v-input__slot {
  min-height: 48px;
  background-color: #ffffff;
  border-radius: 8px;
}
::v-deep .filtro-input.v-text-field--outlined fieldset { border-color: #cbd5e1; border-width: 2px; }
::v-deep .filtro-input.v-input--is-focused fieldset { border-color: #023449 !important; }
::v-deep .filtro-input .v-text-field__slot input { color: #023449; font-weight: 600; font-size: 0.875rem; }
::v-deep .filtro-input .v-label { color: #64748b; font-size: 0.875rem; }
::v-deep .filtro-input .v-input__prepend-inner { margin-top: 12px; }
.filtro-selecionado { font-size: 0.875rem; font-weight: 600; color: #023449; }
.filtro-item { font-size: 0.875rem; color: #1e293b; display: flex; align-items: center; }
.limpar-filtros-btn { text-transform: none; font-weight: 600; letter-spacing: 0; transition: all 0.2s ease; }
.limpar-filtros-btn:hover { background-color: rgba(100, 116, 139, 0.08); }

/* Skeleton */
.loading-skeleton { min-height: 400px; }
.skeleton-table-header {
  display: flex; gap: 12px; padding: 12px 16px;
  background: linear-gradient(180deg, #fafafa 0%, #f8fafc 100%);
  border-bottom: 2px solid #e2e8f0;
}
.skeleton-header-cell { flex: 1; }
.skeleton-row { display: flex; gap: 12px; padding: 12px 16px; align-items: center; background: #ffffff; border-bottom: 1px solid #f1f5f9; }
.skeleton-cell { display: flex; flex-direction: column; justify-content: center; }
.skeleton-cell-date { width: 100px; }
.skeleton-cell-cliente { width: 300px; }
.skeleton-cell-valor { width: 130px; align-items: center; }
.skeleton-cell-situacao { width: 90px; align-items: center; }
.skeleton-cell-info { width: 130px; }
.skeleton-cell-acoes { width: 110px; flex-direction: row; justify-content: center; }
.skeleton-custom { background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px; }
.skeleton-custom-avatar { background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
.skeleton-custom-button { background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 50%; height: 32px; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
::v-deep .skeleton-custom .v-skeleton-loader__text,
::v-deep .skeleton-custom .v-skeleton-loader__avatar,
::v-deep .skeleton-custom .v-skeleton-loader__button { background: transparent !important; }

/* No Data */
.no-data-message { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: 16px; padding: 40px; }
.no-data-message h3 { margin: 0; color: #023449; font-size: 1.125rem; font-weight: 700; }
.no-data-message p { color: #64748b; font-size: 0.875rem; margin: 0; text-align: center; }

/* Células */
.celula-data { display: flex; flex-direction: column; gap: 4px; }
.data-linha { display: flex; align-items: center; }
.data-texto { font-size: 0.875rem; font-weight: 600; color: #1e293b; }
.hora-texto { font-size: 0.813rem; font-weight: 500; color: #64748b; }
.celula-cliente { padding: 4px 0; }
.cliente-linha-principal { display: flex; align-items: center; }
.cliente-info { flex: 1; min-width: 0; }
.cliente-nome { font-size: 0.88rem; font-weight: 600; color: #1e293b; overflow: hidden; text-overflow: ellipsis; line-height: 1.3; }
.cliente-documento { font-size: 0.813rem; color: #64748b; font-family: 'Roboto Mono', monospace; margin-top: 2px; }
.celula-valor { display: flex; justify-content: center; align-items: center; }
.valor-card-mini { background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px 12px; transition: all 0.2s ease; }
.valor-card-mini:hover { border-color: #cbd5e1; box-shadow: 0 2px 8px rgba(2, 52, 73, 0.06); transform: translateY(-1px); }
.valor-card-mini.limite-atual { background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border-color: #a7f3d0; }
.valor-content { display: flex; align-items: baseline; gap: 4px; font-family: 'Roboto Mono', monospace; }
.valor-moeda { font-size: 0.688rem; font-weight: 600; color: #64748b; }
.valor-numero { font-size: 0.938rem; font-weight: 700; color: #023449; letter-spacing: -0.02em; }
.limite-atual .valor-numero { color: #059669; }
.situacao-wrapper { display: inline-block; }
.situacao-avatar { cursor: pointer; transition: all 0.2s ease; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
.situacao-avatar:hover { transform: scale(1.1); box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
.celula-usuario { display: flex; }
.usuario-texto { font-size: 0.875rem; font-weight: 500; color: #475569; }
.sentence-case { text-transform: lowercase; }
.sentence-case::first-letter { text-transform: uppercase; }
.celula-filial { display: flex; }
.celula-acoes { display: flex; align-items: center; justify-content: center; gap: 4px; }
.action-btn { background: #ffffff !important; border: 1px solid #e2e8f0; transition: all 0.2s ease; }
.action-btn:hover { transform: scale(1.1); background: #f8fafc !important; border-color: #cbd5e1; }
.action-btn-reav:not(:disabled) { border-color: #ddd6fe !important; }
.action-btn-reav:not(:disabled):hover { background: #f5f3ff !important; border-color: #c4b5fd !important; }

/* Tooltips */
.tooltip-content { text-align: center; padding: 2px 4px; }
.tooltip-title { font-size: 0.813rem; font-weight: 600; margin-bottom: 2px; }
.tooltip-subtitle { font-size: 0.688rem; opacity: 0.9; }

/* Tabela */
::v-deep .historico-table { font-family: inherit; }
::v-deep .historico-table .v-data-table-header { background: linear-gradient(180deg, #fafafa 0%, #f8fafc 100%); border-bottom: 2px solid #e2e8f0; }
::v-deep .historico-table th { color: #023449 !important; font-weight: 700 !important; font-size: 0.75rem !important; text-transform: uppercase; letter-spacing: 0.5px; padding: 16px 12px !important; white-space: nowrap; }
::v-deep .historico-table tbody tr { transition: all 0.15s ease; border-bottom: 1px solid #f1f5f9; }
::v-deep .historico-table tbody tr:hover { background: linear-gradient(90deg, rgba(2,52,73,0.02) 0%, rgba(2,52,73,0.04) 50%, rgba(2,52,73,0.02) 100%) !important; box-shadow: 0 2px 8px rgba(2,52,73,0.04); }
::v-deep .historico-table tbody td { font-size: 0.875rem; color: #334155; padding: 14px 12px !important; }
::v-deep .historico-table tbody tr:last-child { border-bottom: none; }
::v-deep .historico-table tbody tr:last-child td { border-bottom: none !important; }
::v-deep .v-data-footer { border-top: 2px solid #e2e8f0; background-color: #fafafa; padding: 8px 16px; }

/* ── Dialog de Reavaliação ── */
.reav-card { border-radius: 16px !important; overflow: hidden; }

.reav-header {
  background: linear-gradient(135deg, #472dc6 0%, #6d4ed4 100%);
  padding: 20px 20px 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}
.reav-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.reav-header-titulo {
  font-size: 1.063rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}
.reav-header-subtitulo {
  font-size: 0.813rem;
  color: rgba(255,255,255,0.75);
  margin-top: 2px;
  text-transform: uppercase;
}

.reav-body { padding: 24px 24px 16px !important; }

.reav-info-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
}
.reav-info-row { display: flex; gap: 24px; flex-wrap: wrap; }
.reav-info-item { display: flex; flex-direction: column; gap: 2px; }
.reav-info-label { font-size: 0.688rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
.reav-info-valor { font-size: 0.875rem; font-weight: 600; color: #1e293b; }
.reav-valor-destaque { color: #472dc6; font-family: 'Roboto Mono', monospace; }

.reav-justificativa-label {
  display: flex;
  align-items: center;
  font-size: 0.813rem;
  font-weight: 700;
  color: #1e293b;
}
.reav-obrigatorio { color: #ef4444; margin-left: 2px; }

::v-deep .reav-textarea .v-input__slot { border-radius: 10px !important; }
::v-deep .reav-textarea.v-text-field--outlined fieldset { border-color: #cbd5e1; border-width: 2px; }
::v-deep .reav-textarea.v-input--is-focused fieldset { border-color: #472dc6 !important; }

.reav-aviso {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.813rem;
  color: #92400e;
  margin-top: 12px;
  line-height: 1.5;
}

.reav-actions { padding: 16px 24px !important; }
.reav-btn-cancelar { text-transform: none; font-weight: 600; letter-spacing: 0; border-radius: 8px; }
.reav-btn-confirmar { text-transform: none; font-weight: 600; letter-spacing: 0; border-radius: 8px; padding: 0 20px !important; }

@media (max-width: 768px) {
  .historico-card { border-radius: 8px; }
  .historico-title { font-size: 1rem; }
  .filtro-section { gap: 8px; }
  .skeleton-row { flex-wrap: wrap; }
  .situacao-avatar { width: 36px !important; min-width: 36px !important; height: 36px !important; }
  .cliente-nome { font-size: 0.875rem; }
  .valor-moeda { font-size: 0.625rem; }
  .valor-numero { font-size: 0.813rem; }
  ::v-deep .historico-table th { font-size: 0.688rem !important; padding: 12px 8px !important; }
  ::v-deep .historico-table tbody td { padding: 10px 8px !important; }
  .cliente-info { max-width: 200px; }
  .reav-info-row { flex-direction: column; gap: 12px; }
}
</style>